import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useFairStore } from 'src/store/benefits/fair.store';
import * as Yup from 'yup';

const STATUS_OPTIONS = ['active', 'inactive'];
const STATUS_LABEL = ['Ativo', 'Inativo'];

export default function useFairDetails() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const {
    loadingFairDetails,
    fairDetails,
    getFairDetails,
    resetFairDetails,
    updateFair,
    createFair,
  } = useFairStore();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const currentDate = new Date();

  useEffect(() => {
    const fetchDetails = async () => {
      if (id) {
        try {
          await getFairDetails(id);
        } catch (error) {
          enqueueSnackbar(error.message, { variant: 'error' });
          navigate(paths.dashboard.fairList);
        }
      } else {
        resetFairDetails();
      }
    };

    fetchDetails();
  }, []);

  const defaultValues = useMemo(() => ({
    name: fairDetails?.name || '',
    startDate: fairDetails?.startDate ? new Date(fairDetails.startDate) : new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()),
    endDate: fairDetails?.endDate ? new Date(fairDetails.endDate) : new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()),
    status: fairDetails?.status || 'active',
  }), [fairDetails]);

  const fairSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    startDate: Yup.date().required('Data de início obrigatória!'),
    endDate: Yup.date()
      .required('Data de fim obrigatória!')
      .min(Yup.ref('startDate'), 'Data de fim deve ser após a data de início!'),
    status: Yup.string()
  });

  const methods = useForm({
    resolver: yupResolver(fairSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
    control,
  } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [fairDetails]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const updatedData = {
        name: data.name,
        startDate: data.startDate,
        endDate: data.endDate,
        status: data.status,
      };

      if (isEdit) {
        await updateFair(id || '', updatedData);
        enqueueSnackbar('Feira atualizada com sucesso!', { variant: 'success' });
      } else {
        await createFair(updatedData);
        enqueueSnackbar('Feira criada com sucesso!', { variant: 'success' });
      }
      navigate(paths.dashboard.fairList);

    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        enqueueSnackbar(isEdit ? 'Erro ao editar feira!' : 'Erro ao criar feira!', { variant: 'error' });
      }
    }
  });


  return {
    isEdit,
    loadingFairDetails,
    methods,
    control,
    isSubmitting,
    STATUS_OPTIONS,
    STATUS_LABEL,
    onSubmit,
    navigate,
    enqueueSnackbar,
  };
}
